import { useState } from "react"
import { ProSidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar"
import { Box, IconButton, Typography, useTheme, useMediaQuery, Drawer } from "@mui/material"
import { Link } from "react-router-dom"
import "react-pro-sidebar/dist/css/styles.css"
import { tokens } from "../../theme"
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined"
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat'
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl'
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet'
import PhishingIcon from '@mui/icons-material/Phishing'
import PersonIcon from '@mui/icons-material/Person'
import AddTaskIcon from '@mui/icons-material/AddTask'
import BusinessIcon from '@mui/icons-material/Business'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import SearchIcon from '@mui/icons-material/Search'
import ManageSearchIcon from '@mui/icons-material/ManageSearch'
import OutboxIcon from '@mui/icons-material/Outbox'
import AssignmentIcon from '@mui/icons-material/Assignment'
import NotificationsIcon from '@mui/icons-material/Notifications'
import PeopleIcon from '@mui/icons-material/People'
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety'
import StorageIcon from '@mui/icons-material/Storage'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import SettingsIcon from '@mui/icons-material/Settings'
import { useAuthState } from "../hooks/useAuthState"

type ItemProps = {
  title: string
  to: string
  icon: React.ReactNode
  selected: string
  setSelected: (title: string) => void
  onItemClick?: () => void
}

const Item = ({ title, to, icon, selected, setSelected, onItemClick }: ItemProps) => {
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)

  const handleClick = () => {
    setSelected(title)
    if (onItemClick) {
      onItemClick()
    }
  }

  return (
    <MenuItem
      active={selected === title}
      style={{
        color: colors.grey[100],
      }}
      onClick={handleClick}
      icon={icon}
    >
      <Typography>{title}</Typography>
      <Link to={to} />
    </MenuItem>
  )
}

type SidebarProps = {
  mobileOpen?: boolean
  onMobileClose?: () => void
}

const Sidebar = ({ mobileOpen = false, onMobileClose }: SidebarProps) => {
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [selected, setSelected] = useState("Dashboard")
  const [isCollapsed, setIsCollapsed] = useState(false)
  const { isSignedIn } = useAuthState()

  const handleMenuItemClick = () => {
    if (isMobile && onMobileClose) {
      onMobileClose()
    }
  }

  // サイドバーコンテンツ（デスクトップ・モバイル共通、最小化機能あり）
  const sidebarContent = (
    <Box
      sx={{
        height: "100%",
        "& .pro-sidebar-inner": {
          background: `${colors.primary[400]} !important`,
        },
        "& .pro-icon-wrapper": {
          backgroundColor: "transparent !important",
        },
        "& .pro-inner-item": {
          padding: "5px 35px 5px 20px !important",
        },
        "& .pro-inner-item:hover": {
          color: "#868dfb !important",
        },
        "& .pro-menu-item.active": {
          color: "#6870fa !important",
        },
      }}
    >
      <ProSidebar collapsed={isCollapsed}>
        <Menu iconShape="square">
          {/* LOGO AND MENU ICON */}
          <MenuItem
            onClick={() => setIsCollapsed(!isCollapsed)}
            icon={isCollapsed ? <MenuOutlinedIcon /> : undefined}
            style={{
              margin: "10px 0 20px 0",
              color: colors.grey[100],
            }}
          >
            {!isCollapsed && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                ml="15px"
              >
                <Typography variant="h5" color={colors.grey[100]}>
                  りんどうトレーサビリティ
                </Typography>
                <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
                  <MenuOutlinedIcon />
                </IconButton>
              </Box>
            )}
          </MenuItem>

          <Box paddingLeft={isCollapsed ? undefined : "10%"}>
            <Item
              title="個体受入登録"
              to="/admin/individual/add"
              icon={<AddTaskIcon />}
              selected={selected}
              setSelected={setSelected}
              onItemClick={handleMenuItemClick}
            />
            <Item
              title="受入個体一覧"
              to="/admin/individuals"
              icon={<ChecklistRtlIcon />}
              selected={selected}
              setSelected={setSelected}
              onItemClick={handleMenuItemClick}
            />
            <Item
              title="枝肉一覧（熟成庫）"
              to="/admin/carcass"
              icon={<PhishingIcon />}
              selected={selected}
              setSelected={setSelected}
              onItemClick={handleMenuItemClick}
            />
            <Item
              title="製品一覧（出荷先）"
              to="/admin/products"
              icon={<LocalShippingIcon />}
              selected={selected}
              setSelected={setSelected}
              onItemClick={handleMenuItemClick}
            />
            <Item
              title="ミンチ製品一覧"
              to="/admin/mince-products"
              icon={<LocalShippingIcon />}
              selected={selected}
              setSelected={setSelected}
              onItemClick={handleMenuItemClick}
            />
            <Item
              title="出荷一覧"
              to="/admin/shipments"
              icon={<LocalShippingIcon />}
              selected={selected}
              setSelected={setSelected}
              onItemClick={handleMenuItemClick}
            />
            <Item
              title="商品番号検索"
              to="/admin/search/product"
              icon={<SearchIcon />}
              selected={selected}
              setSelected={setSelected}
              onItemClick={handleMenuItemClick}
            />
            <Item
              title="商品検索"
              to="/admin/search/items"
              icon={<ManageSearchIcon />}
              selected={selected}
              setSelected={setSelected}
              onItemClick={handleMenuItemClick}
            />
            <Item
              title="冷蔵温度推移"
              to="/admin/devices/refrigeration"
              icon={<DeviceThermostatIcon />}
              selected={selected}
              setSelected={setSelected}
              onItemClick={handleMenuItemClick}
            />
            {/* @ts-expect-error - SubMenu type definition issue with react-pro-sidebar */}
            <SubMenu
              title="衛生管理記録"
              icon={<HealthAndSafetyIcon />}
            >
              <Item
                title="衛生管理点検票（日次）"
                to="/admin/daily-hygiene-management"
                icon={<AssignmentIcon />}
                selected={selected}
                setSelected={setSelected}
                onItemClick={handleMenuItemClick}
              />
              <Item
                title="衛生管理点検票（週次）"
                to="/admin/weekly-hygiene-management"
                icon={<AssignmentIcon />}
                selected={selected}
                setSelected={setSelected}
                onItemClick={handleMenuItemClick}
              />
              <Item
                title="衛生管理点検票（月次）"
                to="/admin/hygiene-management"
                icon={<AssignmentIcon />}
                selected={selected}
                setSelected={setSelected}
                onItemClick={handleMenuItemClick}
              />
              <Item
                title="従業員健康チェック表"
                to="/admin/health-check"
                icon={<AssignmentIcon />}
                selected={selected}
                setSelected={setSelected}
                onItemClick={handleMenuItemClick}
              />
              <Item
                title="そ族昆虫防除記録"
                to="/admin/pest-control-management"
                icon={<AssignmentIcon />}
                selected={selected}
                setSelected={setSelected}
                onItemClick={handleMenuItemClick}
              />
            </SubMenu>
            {/* @ts-expect-error - SubMenu type definition issue with react-pro-sidebar */}
            <SubMenu
              title="マスタデータ"
              icon={<StorageIcon />}
            >
              <Item
                title="狩猟者一覧"
                to="/admin/hunters"
                icon={<PersonIcon />}
                selected={selected}
                setSelected={setSelected}
                onItemClick={handleMenuItemClick}
              />
              <Item
                title="取引先一覧"
                to="/admin/customers"
                icon={<BusinessIcon />}
                selected={selected}
                setSelected={setSelected}
                onItemClick={handleMenuItemClick}
              />
              <Item
                title="従業員マスタ"
                to="/admin/employees"
                icon={<PeopleIcon />}
                selected={selected}
                setSelected={setSelected}
                onItemClick={handleMenuItemClick}
              />
              <Item
                title="通知カテゴリ管理"
                to="/admin/notification-categories"
                icon={<NotificationsIcon />}
                selected={selected}
                setSelected={setSelected}
                onItemClick={handleMenuItemClick}
              />
              <Item
                title="休業日マスタ"
                to="/admin/business-holidays"
                icon={<EventBusyIcon />}
                selected={selected}
                setSelected={setSelected}
                onItemClick={handleMenuItemClick}
              />
              <Item
                title="部位マスタ"
                to="/admin/parts"
                icon={<AssignmentIcon />}
                selected={selected}
                setSelected={setSelected}
                onItemClick={handleMenuItemClick}
              />
              <Item
                title="未出荷リスト"
                to="/admin/shipping/register"
                icon={<OutboxIcon />}
                selected={selected}
                setSelected={setSelected}
                onItemClick={handleMenuItemClick}
              />
            </SubMenu>
            {/* @ts-expect-error - SubMenu type definition issue with react-pro-sidebar */}
            <SubMenu
              title="設定"
              icon={<SettingsIcon />}
            >
              <Item
                title="ユーザー管理"
                to="/admin/users"
                icon={<PeopleIcon />}
                selected={selected}
                setSelected={setSelected}
                onItemClick={handleMenuItemClick}
              />
              <Item
                title="フォーム定義設定"
                to="/admin/config/forms"
                icon={<SettingsEthernetIcon />}
                selected={selected}
                setSelected={setSelected}
                onItemClick={handleMenuItemClick}
              />
              <Item
                title="会社設定"
                to="/admin/settings/company"
                icon={<BusinessIcon />}
                selected={selected}
                setSelected={setSelected}
                onItemClick={handleMenuItemClick}
              />
            </SubMenu>
          </Box>
        </Menu>
      </ProSidebar>
    </Box>
  )


  if (!isSignedIn) {
    return <div/>
  }

  // デスクトップとモバイルの両方を返す
  return (
    <>
      {/* デスクトップ用サイドバー（最小化機能あり） */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        {sidebarContent}
      </Box>

      {/* モバイル用Drawer（最小化機能あり） */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: isCollapsed ? 80 : 270,
            transition: 'width 0.3s ease',
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    </>
  )
}

export default Sidebar
